/**
 * The board's palette and metrics, written once.
 *
 * These are the design's own values rather than the `--v2-*` tokens. The token
 * set carries no amber or blue of the right hue and a warmer grey than this
 * design uses, so mapping onto it approximates — and an approximation is what
 * "does not match the design" means. Holding them here instead of inline keeps
 * the table, the panel, the menus and the chat cards from drifting apart.
 *
 * They are whole Tailwind class strings rather than raw hex because Tailwind
 * reads source files as text: a literal like `bg-[#FBFBFB]` is found here, a
 * class assembled at runtime from a hex constant is not.
 */

/** Hairlines. `LINE` frames a surface; `LINE_SOFT` divides within one. */
export const LINE = 'border-[rgba(0,0,0,0.08)]';

export const LINE_SOFT = 'border-[rgba(0,0,0,0.06)]';

export const LINE_STRONG = 'border-[rgba(0,0,0,0.14)]';

/** Text, from body ink down to a placeholder. */
export const INK = 'text-[#181818]';

export const INK_2 = 'text-[#696969]';

export const INK_3 = 'text-[#9A9A9A]';

/** Surfaces. `SUBTLE` is the hover and header band; `CARD` is the page's white. */
export const CARD = 'bg-white';

export const SUBTLE = 'bg-[#FBFBFB]';

/** A row or card the reader has open, and the rule that marks it. */
export const SELECTED = 'bg-[#FBFAF7]';

export const SELECTED_RULE = 'shadow-[inset_3px_0_0_#9DB9F0]';

/** The warmer hover behind an editable value, which has to read against `SUBTLE`. */
export const FIELD_HOVER = 'hover:bg-[#F6F5F1]';

/** A floating surface: the filter, sort and value menus. */
export const POPOVER = `${CARD} ${LINE} border rounded-[12px] shadow-[0_12px_34px_rgba(0,0,0,0.15)] p-1.5`;

/** A section heading inside the panel. */
export const SECTION_LABEL =
  'text-[11px] uppercase tracking-[0.06em] font-semibold text-[#9A9A9A]';

/**
 * The board's column track.
 *
 * Nine columns with an 18px gutter. The table is held to the sum of their
 * minimums plus the gutters (1528 + 8 × 18) so the last two columns cannot
 * overflow the rounded frame, the header band and the row backgrounds.
 */
export const GRID_COLUMNS = 'grid-cols-[minmax(340px,3.4fr)_128px_minmax(140px,1fr)_minmax(140px,1fr)_minmax(140px,1fr)_150px_150px_minmax(150px,1.1fr)_minmax(190px,1.3fr)]';

export const TABLE_MIN_WIDTH = 'min-w-[1672px]';

/** A label, as a chip. Neutral by design: labels carry no colour of their own. */
export const LABEL_CHIP =
  'inline-block max-w-full truncate rounded-[6px] border border-[rgba(0,0,0,0.06)] bg-[#FBFBFB] px-[9px] py-[3px] text-[12.5px] text-[#696969]';

/** A value the task does not hold, in the table and the panel alike. */
export const EMPTY_VALUE = 'text-[#B7B4AC]';

/**
 * How wide the task panel opens, in the chat canvas and docked on the board
 * alike. Narrower than a report: the panel is a property list, not a document.
 */
export const TASK_PANEL_WIDTH_PX = 480;
